import { useEffect, useRef, useState } from 'react';
import { trackContentEvent } from '@/analytics/analytics';
import { Button } from '@/components/ui/button';
import { Play, ChevronRight, ChevronLeft, CheckCircle, BookOpen, Info, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * WatchContentRenderer - Renders different watch content formats
 * Supports: interactive_animation, infographic, story, timeline, concept_map
 */

// Interactive Animation Renderer
function InteractiveAnimation({ data, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const stepStripRef = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    if (!isPlaying) return;
    const el = stepRefs.current[currentStep];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentStep, isPlaying]);

  const handlePlay = () => {
    setIsPlaying(true);
    setCurrentStep(0);
    setCompletedSteps(new Set());
    try {
      trackContentEvent('content_play', {
        content_id: data.id || null,
        content_type: data.type || 'interactive',
        content_title: data.title || null,
        duration: data.duration || null,
      });
    } catch (e) {}
  };

  const handleNext = () => {
    if (currentStep < data.steps.length - 1) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
    } else {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setIsPlaying(false);
      try {
        trackContentEvent('content_complete', {
          content_id: data.id || null,
          content_type: data.type || 'interactive',
          content_title: data.title || null,
        });
      } catch (e) {}
      if (onComplete) onComplete();
    }
  };

  const handleStepClick = (index) => {
    if (index <= currentStep || completedSteps.has(index - 1)) {
      setCurrentStep(index);
    }
  };

  // Safety: Ensure all values are strings before rendering
  const safeTitle = typeof data.title === 'string' ? data.title : String(data.title || '');
  const safeDescription = typeof data.description === 'string' ? data.description : String(data.description || '');

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 rounded-lg p-6 border border-blue-500/30">
        <h3 className="text-xl font-bold text-white mb-2">{safeTitle}</h3>
        <p className="text-gray-300 text-sm mb-4">{safeDescription}</p>

        {!isPlaying ? (
          <Button
            onClick={handlePlay}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
            size="lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Animation
          </Button>
        ) : (
          <div className="space-y-4">
            {/* Current Step Display */}
            <div className="bg-black/40 rounded-lg p-6 text-center min-h-[200px] flex flex-col items-center justify-center">
              <div className="text-6xl mb-4 animate-bounce">
                {data.steps[currentStep]?.icon}
              </div>
              <h4 className="text-2xl font-bold text-white mb-2">
                {typeof data.steps[currentStep]?.title === 'string' ? data.steps[currentStep].title : String(data.steps[currentStep]?.title || '')}
              </h4>
              <p className="text-gray-300 text-lg">
                {typeof data.steps[currentStep]?.description === 'string' ? data.steps[currentStep].description : String(data.steps[currentStep]?.description || '')}
              </p>
            </div>

            {/* Step Indicators */}
            <div ref={stepStripRef} className="overflow-x-auto max-w-full pb-1">
              <div className="flex gap-3 w-max min-w-full">
                {data.steps.map((step, index) => (
                  <button
                    key={step.id}
                    ref={(node) => {
                      if (node) stepRefs.current[index] = node;
                    }}
                    onClick={() => handleStepClick(index)}
                    className={cn(
                      "flex-none w-32 p-3 rounded-lg border-2 border-gray-700/60 transition-all",
                      // Current step: consistent border thickness + subtle elevation
                      index === currentStep && "relative z-10 bg-blue-500/20 border-blue-400/60 shadow-md",
                      // Completed: subtle green tint
                      index !== currentStep && completedSteps.has(index) && "bg-emerald-500/15 border-emerald-400/50",
                      // Locked: dim
                      index > currentStep && !completedSteps.has(index - 1) && "opacity-40 cursor-not-allowed",
                      // Default/hover
                      (index <= currentStep || completedSteps.has(index - 1))
                        ? "cursor-pointer hover:bg-white/10"
                        : ""
                    )}
                  >
                    <div className="text-2xl mb-1">{step.icon}</div>
                    <div className="text-xs text-gray-300 truncate">{typeof step.title === 'string' ? step.title : String(step.title || '')}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
                disabled={currentStep === 0}
                variant="outline"
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600"
              >
                {currentStep < data.steps.length - 1 ? (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Complete
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Interactive Infographic Renderer
function InteractiveInfographic({ data, onComplete }) {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [viewedSections, setViewedSections] = useState(new Set());

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
        setViewedSections(prev => new Set([...prev, id]));
      }
      return newSet;
    });

    // Check if all sections viewed
    if (viewedSections.size + 1 >= data.sections.length) {
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    }
  };

  // Safety: Ensure all values are strings/numbers before rendering
  const safeTitle = typeof data.title === 'string' ? data.title : String(data.title || '');
  const safeDescription = typeof data.description === 'string' ? data.description : String(data.description || '');
  const safeSections = Array.isArray(data.sections) ? data.sections : [];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-cyan-900/40 to-blue-900/40 rounded-lg p-6 border border-cyan-500/30">
        <h3 className="text-xl font-bold text-white mb-2">{safeTitle}</h3>
        <p className="text-gray-300 text-sm mb-6">{safeDescription}</p>

        <div className="space-y-4">
          {safeSections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const isViewed = viewedSections.has(section.id);

            // Safety: Ensure all section values are strings/numbers
            const safeSectionTitle = typeof section.title === 'string' ? section.title : String(section.title || '');
            const safeSectionContent = typeof section.content === 'string' ? section.content : String(section.content || '');
            const safeSectionColor = typeof section.color === 'string' ? section.color : String(section.color || 'from-cyan-500 to-blue-500');
            const safePercentage = typeof section.percentage === 'number' ? section.percentage : (typeof section.percentage === 'string' ? parseInt(section.percentage) || 0 : 0);

            return (
              <div
                key={section.id}
                className={cn(
                  "rounded-lg border-2 transition-all cursor-pointer overflow-hidden",
                  isExpanded ? "border-cyan-400 bg-cyan-500/20" : "border-gray-600 bg-gray-800/40 hover:border-cyan-500/50",
                  isViewed && !isExpanded && "border-green-500/50"
                )}
                onClick={() => toggleSection(section.id)}
              >
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold",
                      `bg-gradient-to-br ${safeSectionColor}`
                    )}>
                      {safePercentage}%
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white">{safeSectionTitle}</h4>
                      {isExpanded && (
                        <p className="text-gray-300 text-sm mt-1">{safeSectionContent}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isViewed && (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    <ChevronRight className={cn(
                      "w-5 h-5 text-gray-400 transition-transform",
                      isExpanded && "transform rotate-90"
                    )} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 text-center text-sm text-gray-400">
          {viewedSections.size} / {safeSections.length} sections explored
        </div>
      </div>
    </div>
  );
}

// Story-based Learning Renderer
function StoryLearning({ data, onComplete }) {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [readChapters, setReadChapters] = useState(new Set());
  const chapterStripRef = useRef(null);
  const chapterRefs = useRef([]);

  useEffect(() => {
    const el = chapterRefs.current[currentChapter];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentChapter]);

  const handleNextChapter = () => {
    setReadChapters(prev => new Set([...prev, currentChapter]));
    if (currentChapter < data.chapters.length - 1) {
      setCurrentChapter(prev => prev + 1);
    } else {
      if (onComplete) onComplete();
    }
  };

  const handleChapterSelect = (index) => {
    if (index <= currentChapter || readChapters.has(index - 1)) {
      setCurrentChapter(index);
    }
  };

  // Safety: Ensure all values are strings before rendering
  const safeTitle = typeof data.title === 'string' ? data.title : String(data.title || '');
  const safeDescription = typeof data.description === 'string' ? data.description : String(data.description || '');
  const safeChapters = Array.isArray(data.chapters) ? data.chapters : [];
  const currentChapterData = safeChapters[currentChapter];
  const safeChapterTitle = currentChapterData && typeof currentChapterData.title === 'string' ? currentChapterData.title : String(currentChapterData?.title || '');
  const safeChapterContent = currentChapterData && typeof currentChapterData.content === 'string' ? currentChapterData.content : String(currentChapterData?.content || '');

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 rounded-lg p-6 border border-purple-500/30">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-6 h-6 text-purple-400" />
          <h3 className="text-xl font-bold text-white">{safeTitle}</h3>
        </div>
        <p className="text-gray-300 text-sm mb-6">{safeDescription}</p>

        {/* Current Chapter */}
        <div className="bg-black/40 rounded-lg p-6 mb-4 min-h-[250px]">
          <div className="flex items-start gap-4">
            <div className="text-5xl">{currentChapterData?.icon}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-purple-400 font-semibold">
                  Chapter {currentChapter + 1} of {safeChapters.length}
                </span>
              </div>
              <h4 className="text-xl font-bold text-white mb-3">
                {safeChapterTitle}
              </h4>
              <p className="text-gray-300 leading-relaxed text-lg">
                {safeChapterContent}
              </p>
            </div>
          </div>
        </div>

        {/* Chapter Navigation */}
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => setCurrentChapter(prev => Math.max(0, prev - 1))}
            disabled={currentChapter === 0}
            variant="outline"
            className="flex-1"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <Button
            onClick={handleNextChapter}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600"
          >
            {currentChapter < data.chapters.length - 1 ? (
              <>
                Next Chapter
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-1" />
                Finish Story
              </>
            )}
          </Button>
        </div>

        {/* Chapter Progress */}
        <div ref={chapterStripRef} className="overflow-x-auto max-w-full pb-1">
          <div className="flex gap-3 w-max min-w-full">
            {safeChapters.map((chapter, index) => (
              <button
                key={chapter.id}
                ref={(node) => {
                  if (node) chapterRefs.current[index] = node;
                }}
                onClick={() => handleChapterSelect(index)}
                className={cn(
                  "flex-none w-40 p-2 rounded-lg border-2 border-gray-700/60 transition-all text-xs",
                  index === currentChapter && "relative z-10 bg-purple-500/20 border-purple-400/60 shadow-md",
                  index !== currentChapter && readChapters.has(index) && "bg-emerald-500/15 border-emerald-400/50",
                  index > currentChapter && !readChapters.has(index - 1) && "opacity-40 cursor-not-allowed",
                  (index <= currentChapter || readChapters.has(index - 1))
                    ? "cursor-pointer hover:bg-white/10"
                    : ""
                )}
              >
                <div className="text-lg mb-1">{chapter.icon}</div>
                <div className="text-gray-300 truncate">{typeof chapter.title === 'string' ? chapter.title : String(chapter.title || '')}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Timeline Renderer
function TimelineRenderer({ data, onComplete }) {
  const [viewedEvents, setViewedEvents] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setViewedEvents(prev => new Set([...prev, event.id]));

    if (viewedEvents.size + 1 >= data.events.length) {
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 rounded-lg p-6 border border-amber-500/30">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-6 h-6 text-amber-400" />
          <h3 className="text-xl font-bold text-white">{data.title}</h3>
        </div>
        <p className="text-gray-300 text-sm mb-6">{data.description}</p>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-orange-600"></div>

          {/* Events */}
          <div className="space-y-6 relative">
            {data.events.map((event, index) => {
              const isViewed = viewedEvents.has(event.id);
              const isSelected = selectedEvent?.id === event.id;

              return (
                <div
                  key={event.id}
                  className="flex items-start gap-4 relative z-10"
                >
                  {/* Timeline Dot */}
                  <div className={cn(
                    "w-16 h-16 rounded-full flex items-center justify-center text-2xl border-4 transition-all",
                    isSelected ? "bg-amber-500 border-amber-300 scale-110" : 
                    isViewed ? "bg-orange-500/50 border-orange-400" : 
                    "bg-gray-700 border-gray-600"
                  )}>
                    {event.icon}
                  </div>

                  {/* Event Card */}
                  <div
                    onClick={() => handleEventClick(event)}
                    className={cn(
                      "flex-1 bg-black/40 rounded-lg p-4 border-2 cursor-pointer transition-all",
                      isSelected ? "border-amber-400 bg-amber-500/20 scale-105" :
                      isViewed ? "border-orange-500/50 hover:border-orange-400" :
                      "border-gray-600 hover:border-amber-500/50"
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-amber-400">{event.time}</span>
                      {isViewed && <CheckCircle className="w-4 h-4 text-green-400" />}
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-1">{event.title}</h4>
                    <p className="text-gray-300 text-sm">{event.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-400">
          {viewedEvents.size} / {data.events.length} events explored
        </div>
      </div>
    </div>
  );
}

// Concept Map Renderer
function ConceptMapRenderer({ data, onComplete }) {
  const [selectedConcepts, setSelectedConcepts] = useState(new Set());
  const [connections, setConnections] = useState(new Set());

  const handleConceptClick = (concept) => {
    setSelectedConcepts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(concept.id)) {
        newSet.delete(concept.id);
      } else {
        newSet.add(concept.id);
        // Show connections
        concept.connections.forEach(connId => {
          setConnections(prev => new Set([...prev, `${concept.id}-${connId}`]));
        });
      }
      return newSet;
    });

    if (selectedConcepts.size + 1 >= data.concepts.length) {
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-lg p-6 border border-green-500/30">
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-6 h-6 text-green-400" />
          <h3 className="text-xl font-bold text-white">{data.title}</h3>
        </div>
        <p className="text-gray-300 text-sm mb-6">{data.description}</p>

        <div className="relative bg-black/40 rounded-lg p-8 min-h-[400px]">
          {/* SVG for connections */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {data.concepts.map(concept => 
              concept.connections.map(connId => {
                const target = data.concepts.find(c => c.id === connId);
                if (!target) return null;
                const isVisible = connections.has(`${concept.id}-${connId}`) || 
                                 connections.has(`${connId}-${concept.id}`);
                return (
                  <line
                    key={`${concept.id}-${connId}`}
                    x1={`${concept.position.x}%`}
                    y1={`${concept.position.y}%`}
                    x2={`${target.position.x}%`}
                    y2={`${target.position.y}%`}
                    stroke={isVisible ? "#10b981" : "#374151"}
                    strokeWidth={isVisible ? "3" : "1"}
                    strokeDasharray={isVisible ? "0" : "5,5"}
                    className="transition-all duration-500"
                  />
                );
              })
            )}
          </svg>

          {/* Concepts */}
          {data.concepts.map(concept => {
            const isSelected = selectedConcepts.has(concept.id);
            return (
              <button
                key={concept.id}
                onClick={() => handleConceptClick(concept)}
                className={cn(
                  "absolute transform -translate-x-1/2 -translate-y-1/2 transition-all",
                  isSelected ? "scale-110 z-10" : "hover:scale-105"
                )}
                style={{
                  left: `${concept.position.x}%`,
                  top: `${concept.position.y}%`
                }}
              >
                <div className={cn(
                  "w-20 h-20 rounded-full flex flex-col items-center justify-center border-4 transition-all",
                  isSelected ? "bg-green-500/30 border-green-400 shadow-lg shadow-green-500/50" :
                  "bg-gray-700/50 border-gray-600 hover:border-green-500/50"
                )}>
                  <div className="text-3xl mb-1">{concept.icon || "🔗"}</div>
                  <div className="text-xs text-white font-semibold text-center px-1">
                    {concept.title}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 text-center text-sm text-gray-400">
          Click on concepts to explore connections
        </div>
      </div>
    </div>
  );
}

/**
 * Main WatchContentRenderer component
 * Routes to appropriate renderer based on format_type
 */
export default function WatchContentRenderer({ watchContent, onComplete }) {
  console.log('🎬 WatchContentRenderer received:', watchContent);
  
  if (!watchContent || !watchContent.content_data) {
    console.warn('⚠️ WatchContentRenderer: Missing watchContent or content_data', {
      watchContent,
      hasContentData: !!watchContent?.content_data
    });
    return (
      <div className="text-center text-gray-400 py-8">
        <p>No watch content available</p>
        {watchContent && !watchContent.content_data && (
          <p className="text-xs mt-2">Content data is missing</p>
        )}
      </div>
    );
  }

  const { format_type, title, description, content_data } = watchContent;
  console.log('📋 WatchContentRenderer: Rendering format:', format_type, 'Title:', title);
  
  // Safety: Ensure title and description are strings, not objects
  const safeTitle = typeof title === 'string' ? title : (title ? String(title) : '');
  const safeDescription = typeof description === 'string' ? description : (description ? String(description) : '');
  
  // Prepare data object for renderers
  const data = {
    title: safeTitle,
    description: safeDescription,
    ...content_data
  };

  switch (format_type) {
    case 'interactive_animation':
      return <InteractiveAnimation data={data} onComplete={onComplete} />;
    case 'infographic':
      return <InteractiveInfographic data={data} onComplete={onComplete} />;
    case 'story':
      return <StoryLearning data={data} onComplete={onComplete} />;
    case 'timeline':
      return <TimelineRenderer data={data} onComplete={onComplete} />;
    case 'concept_map':
      return <ConceptMapRenderer data={data} onComplete={onComplete} />;
    default:
      return (
        <div className="text-center text-gray-400 py-8">
          <p>Unsupported format type: {format_type}</p>
        </div>
      );
  }
}

